<template>
    <div class="KbSearchBox">
        <div class="KbSearchBox__inputWrapper">
            <EidIcon class="KbSearchBox__icon" :icon-src="faMagnifyingGlass" />
            <input
                class="KbSearchBox__input"
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-haspopup="listbox"
                aria-controls="KbSearchBox__listbox"
                aria-label="Search help articles"
                :aria-expanded="showPanel ? 'true' : 'false'"
                :aria-activedescendant="activeResultId ?? undefined"
                :value="query"
                :maxlength="MAX_QUERY_LENGTH"
                placeholder="Search help articles"
                @input="onInput"
                @keydown="onKeydown"
            >
        </div>
        <div v-if="showPanel" class="KbSearchBox__results">
            <div v-if="isLoading" class="KbSearchBox__loading">
                Searching…
            </div>
            <div v-else-if="hasError" class="KbSearchBox__error">
                We could not search help articles right now.
            </div>
            <div v-else-if="showEmpty" class="KbSearchBox__empty">
                No matching articles found.
            </div>
            <ul v-else-if="hasResults" id="KbSearchBox__listbox" class="KbSearchBox__list" role="listbox">
                <li
                    v-for="(result, index) in results"
                    :id="resultDomId(result)"
                    :key="result.id"
                    class="KbSearchBox__result"
                    :class="{ 'KbSearchBox__result--focused': index === focusedIndex }"
                    role="option"
                    :aria-selected="index === focusedIndex ? 'true' : 'false'"
                    @click="selectResult(result)"
                >
                    <span class="KbSearchBox__resultTitle">{{ result.title }}</span>
                    <span class="KbSearchBox__resultSnippet" v-html="sanitizeSnippet(result.snippet)" />
                </li>
            </ul>
        </div>
    </div>
</template>

<style lang="scss">
.KbSearchBox {
    position: relative;

    &__inputWrapper {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        background: var(--eidWhite);
        border: 1px solid var(--eidTechGray-25);
        border-radius: 4px;
        color: var(--eidSecureBlue-75);
    }

    &__icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    &__input {
        flex: 1;
        border: none;
        outline: none;
        font-size: var(--text-body-size, 14px);
        color: var(--eidSecureBlue);

        &::placeholder {
            color: var(--eidSecureBlue-75);
        }
    }

    &__results {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 1;
        margin-top: 4px;
        background: var(--eidWhite);
        border: 1px solid var(--eidTechGray-25);
        border-radius: 4px;
        overflow: hidden;
    }

    &__loading,
    &__error,
    &__empty {
        padding: 12px 16px;
        font-size: var(--text-body-size, 14px);
        color: var(--eidSecureBlue-75);
    }

    &__list {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__result {
        padding: 10px 16px;
        cursor: pointer;
        border-bottom: 1px solid var(--eidTechGray-10);

        &:last-child {
            border-bottom: none;
        }

        &:hover,
        &--focused {
            background-color: var(--eidTechGray-10);
        }
    }

    &__resultTitle {
        display: block;
        font-size: var(--text-body-size, 14px);
        font-weight: 500;
        color: var(--eidSecureBlue);
    }

    &__resultSnippet {
        display: block;
        margin-top: 2px;
        font-size: var(--text-body-size, 14px);
        color: var(--eidSecureBlue-75);

        em, strong, b, i {
            color: var(--eidSecureBlue);
            font-style: normal;
            font-weight: 700;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import DOMPurify from 'dompurify';
    import debounce from 'lodash/debounce';

    import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { KbSearchResult } from '@evidentid/tprm-portal-lib/models/kb';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import { useKbStore } from '@/modules/kb/store';

    // The backend rejects a query longer than this with a 400, so the input stops the
    // user from reaching that error rather than surfacing it after the fact.
    const MAX_QUERY_LENGTH = 200;

    // The search endpoint is uncached, rate-limited, and shared by the whole tenant
    // through one credential, so a request per keystroke is a real cost rather than a
    // theoretical one.
    const SEARCH_DEBOUNCE_MS = 300;

    export default defineComponent({
        name: 'KbSearchBox',
        components: { EidIcon },
        emits: [ 'select' ],
        data() {
            return {
                faMagnifyingGlass,
                MAX_QUERY_LENGTH,
                focusedIndex: 0,
                debouncedSearch: null as ((query: string) => void) | null,
            };
        },
        computed: {
            kbStore() {
                return useKbStore();
            },
            // The store, not local component state, is the source of truth for the
            // query text: it is what survives the component being unmounted and
            // remounted while the store itself lives on.
            query(): string {
                return this.kbStore.searchQuery;
            },
            results(): KbSearchResult[] {
                return this.kbStore.searchResults;
            },
            hasResults(): boolean {
                return this.results.length > 0;
            },
            isLoading(): boolean {
                return this.kbStore.searchStatus === OperationStatus.loading;
            },
            hasError(): boolean {
                return this.kbStore.searchStatus === OperationStatus.error;
            },
            showEmpty(): boolean {
                return this.kbStore.searchStatus === OperationStatus.success && !this.hasResults;
            },
            // The results panel stays hidden until there is something to say about the
            // search: a fresh, empty input shows neither a spinner nor a "no matches".
            showPanel(): boolean {
                return this.isLoading || this.hasError || this.showEmpty || this.hasResults;
            },
            // The id of the result the user has reached via the arrow keys, or null
            // when there is nothing to point the input's aria-activedescendant at.
            activeResultId(): string | null {
                if (!this.hasResults || this.focusedIndex < 0 || this.focusedIndex >= this.results.length) {
                    return null;
                }
                return this.resultDomId(this.results[this.focusedIndex]);
            },
        },
        watch: {
            // A fresh set of results always starts with the first row focused, the
            // same convention AutoComplete.vue uses for its match list.
            results(): void {
                this.focusedIndex = 0;
            },
        },
        created(): void {
            this.debouncedSearch = debounce((query: string) => {
                this.kbStore.search(query);
            }, SEARCH_DEBOUNCE_MS);
        },
        mounted(): void {
            document.addEventListener('click', this.onDocumentClick);
        },
        beforeUnmount(): void {
            document.removeEventListener('click', this.onDocumentClick);
            (this.debouncedSearch as any)?.cancel();
        },
        methods: {
            onInput(event: Event): void {
                const value = (event.target as HTMLInputElement).value;
                this.kbStore.setQuery(value);
                if (!value.trim()) {
                    // Cancel a pending debounced call so a slow response for a query the
                    // user already cleared cannot repopulate results after the fact.
                    (this.debouncedSearch as any)?.cancel();
                    this.kbStore.search(value);
                    return;
                }
                this.debouncedSearch?.(value);
            },
            onKeydown(event: KeyboardEvent): void {
                if (event.key === 'Escape') {
                    this.kbStore.clearResults();
                    return;
                }
                if (!this.hasResults) {
                    return;
                }
                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault();
                        this.focusedIndex = Math.min(this.focusedIndex + 1, this.results.length - 1);
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
                        break;
                    case 'Enter':
                        event.preventDefault();
                        this.selectResult(this.results[this.focusedIndex]);
                        break;
                }
            },
            // Closes the dropdown for a click anywhere outside the search box, the same
            // way Escape and selecting a result do.
            onDocumentClick(event: MouseEvent): void {
                if (this.showPanel && !(this.$el as HTMLElement).contains(event.target as Node)) {
                    this.kbStore.clearResults();
                }
            },
            selectResult(result: KbSearchResult): void {
                this.$emit('select', result.id);
                this.kbStore.clearResults();
            },
            resultDomId(result: KbSearchResult): string {
                return `KbSearchBox__option-${result.id}`;
            },
            // The backend already sanitizes; this pass is defence in depth because the
            // result is handed to v-html. A snippet carries only the Zendesk highlight
            // tags the backend's own sanitizer already limits itself to.
            sanitizeSnippet(snippet: string): string {
                return DOMPurify.sanitize(snippet, { ALLOWED_TAGS: [ 'em', 'strong', 'b', 'i' ] });
            },
        },
    });
</script>
